import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { statsAPI, settingsAPI } from '../services/api';
import DOMPurify from 'dompurify';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Underline } from '@tiptap/extension-underline';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  MobileStepper,
  Skeleton,
  Snackbar,
  Alert,
  Divider,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Switch,
  FormControlLabel,
  useTheme,
} from '@mui/material';
import {
  ShoppingBag as ShoppingBagIcon,
  AddShoppingCart as AddShoppingCartIcon,
  Inventory as InventoryIcon,
  People as PeopleIcon,
  PendingActions as PendingIcon,
  TrendingUp as RevenueIcon,
  Group as GroupIcon,
  ListAlt as ListIcon,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
} from '@mui/icons-material';

// ── Rich-text editor toolbar ───────────────────────────────────────────────────
const EditorToolbar = ({ editor }) => {
  if (!editor) return null;

  const setLink = () => {
    const url = window.prompt('URL', editor.getAttributes('link').href ?? 'https://');
    if (url === null) return;
    if (url === '') { editor.chain().focus().extendMarkRange('link').unsetLink().run(); return; }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url, target: '_blank' }).run();
  };

  return (
    <Box
      sx={{
        display: 'flex', flexWrap: 'wrap', gap: 0.5, p: 1,
        borderBottom: '1px solid', borderColor: 'divider',
        bgcolor: 'grey.50',
      }}
    >
      <ToggleButtonGroup size="small" exclusive={false}>
        <Tooltip title="Bold"><ToggleButton value="bold" selected={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}><FormatBold fontSize="small" /></ToggleButton></Tooltip>
        <Tooltip title="Italic"><ToggleButton value="italic" selected={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}><FormatItalic fontSize="small" /></ToggleButton></Tooltip>
        <Tooltip title="Underline"><ToggleButton value="underline" selected={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()}><FormatUnderlined fontSize="small" /></ToggleButton></Tooltip>
      </ToggleButtonGroup>

      <Divider orientation="vertical" flexItem />

      <ToggleButtonGroup size="small" exclusive>
        <Tooltip title="Heading 2"><ToggleButton value="h2" selected={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}><Typography variant="caption" sx={{ fontWeight: 700, fontSize: 13 }}>H2</Typography></ToggleButton></Tooltip>
        <Tooltip title="Heading 3"><ToggleButton value="h3" selected={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}><Typography variant="caption" sx={{ fontWeight: 700, fontSize: 13 }}>H3</Typography></ToggleButton></Tooltip>
      </ToggleButtonGroup>

      <Divider orientation="vertical" flexItem />

      <ToggleButtonGroup size="small" exclusive={false}>
        <Tooltip title="Bullet list"><ToggleButton value="ul" selected={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}><FormatListBulleted fontSize="small" /></ToggleButton></Tooltip>
        <Tooltip title="Numbered list"><ToggleButton value="ol" selected={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}><FormatListNumbered fontSize="small" /></ToggleButton></Tooltip>
      </ToggleButtonGroup>

      <Divider orientation="vertical" flexItem />

      <ToggleButtonGroup size="small" exclusive>
        <Tooltip title="Align left"><ToggleButton value="left" selected={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()}><FormatAlignLeft fontSize="small" /></ToggleButton></Tooltip>
        <Tooltip title="Align center"><ToggleButton value="center" selected={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()}><FormatAlignCenter fontSize="small" /></ToggleButton></Tooltip>
        <Tooltip title="Align right"><ToggleButton value="right" selected={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()}><FormatAlignRight fontSize="small" /></ToggleButton></Tooltip>
      </ToggleButtonGroup>

      <Divider orientation="vertical" flexItem />

      <Tooltip title="Set link"><ToggleButton size="small" value="link" selected={editor.isActive('link')} onClick={setLink}><LinkIcon fontSize="small" /></ToggleButton></Tooltip>
      <Tooltip title="Remove link"><ToggleButton size="small" value="unlink" disabled={!editor.isActive('link')} onClick={() => editor.chain().focus().unsetLink().run()}><LinkOffIcon fontSize="small" /></ToggleButton></Tooltip>
    </Box>
  );
};

const banners = [
  { img: '/Banner1.png', alt: 'Banner 1' },
  { img: '/Banner2.jpeg', alt: 'Banner 2' },
  { img: '/Banner3.jpeg', alt: 'Banner 3' },
  { img: '/Banner4.jpeg', alt: 'Banner 4' },
  { img: '/Banner5.jpeg', alt: 'Banner 5' },
];

const Home = () => {
  const { user, hasRole } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [homeMessage, setHomeMessage] = useState(null); // stored HTML
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [orderingEnabled, setOrderingEnabled] = useState(true);
  const [orderingSaving, setOrderingSaving] = useState(false);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false, HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle,
      Color,
    ],
    content: '',
    editorProps: {
      attributes: {
        style: 'min-height: 120px; padding: 12px; outline: none; font-family: inherit; font-size: 1rem;',
      },
    },
  });

  // Auto-rotate banners
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % banners.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  // Load dashboard KPIs for admin/operator
  useEffect(() => {
    if (!hasRole(['admin', 'operator'])) return;
    setStatsLoading(true);
    statsAPI.getDashboard()
      .then((res) => setStats(res.data))
      .catch((err) => console.error('Failed to load stats', err))
      .finally(() => setStatsLoading(false));
  }, []);

  // Load home message
  useEffect(() => {
    settingsAPI.get('home_message')
      .then((res) => setHomeMessage(res.data.value))
      .catch(() => setHomeMessage(null));
  }, []);

  // Load ordering on/off state (admin only)
  useEffect(() => {
    if (!hasRole('admin')) return;
    settingsAPI.get('ordering_enabled')
      .then((res) => setOrderingEnabled(res.data.value !== 'false'))
      .catch(() => setOrderingEnabled(true));
  }, []);

  const toggleOrdering = async (nextEnabled) => {
    setOrderingSaving(true);
    const prev = orderingEnabled;
    setOrderingEnabled(nextEnabled);
    try {
      await settingsAPI.update('ordering_enabled', nextEnabled ? 'true' : 'false');
      setSnackbar({
        open: true,
        message: nextEnabled ? t('home.orderingOpened') : t('home.orderingClosed'),
        severity: 'success',
      });
    } catch {
      setOrderingEnabled(prev);
      setSnackbar({ open: true, message: t('home.orderingSaveError'), severity: 'error' });
    } finally {
      setOrderingSaving(false);
    }
  };

  const startEditing = useCallback(() => {
    if (editor) editor.commands.setContent(homeMessage ?? '');
    setEditing(true);
  }, [editor, homeMessage]);

  const cancelEditing = () => setEditing(false);

  const saveMessage = async () => {
    if (!editor) return;
    setSaving(true);
    try {
      const html = editor.getHTML();
      await settingsAPI.update('home_message', html);
      setHomeMessage(html);
      setEditing(false);
      setSnackbar({ open: true, message: t('home.messageSaved'), severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: t('home.messageSaveError'), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => setActiveStep((prev) => (prev + 1) % banners.length);
  const handleBack = () => setActiveStep((prev) => (prev - 1 + banners.length) % banners.length);

  const quickActions = [
    {
      title: t('home.newOrderTitle'),
      description: t('home.newOrderDesc'),
      icon: <AddShoppingCartIcon sx={{ fontSize: 40 }} />,
      path: '/new-order',
      color: theme.palette.secondary.main,
      roles: ['admin', 'operator', 'client'],
    },
    {
      title: t('home.myOrdersTitle'),
      description: t('home.myOrdersDesc'),
      icon: <ShoppingBagIcon sx={{ fontSize: 40 }} />,
      path: '/orders',
      color: theme.palette.primary.main,
      roles: ['admin', 'operator', 'client'],
    },
    {
      title: t('home.productsTitle'),
      description: t('home.productsDesc'),
      icon: <InventoryIcon sx={{ fontSize: 40 }} />,
      path: '/products',
      color: '#4caf50',
      roles: ['admin', 'operator'],
    },
    {
      title: t('home.clientsTitle'),
      description: t('home.clientsDesc'),
      icon: <PeopleIcon sx={{ fontSize: 40 }} />,
      path: '/clients',
      color: '#2196f3',
      roles: ['admin', 'operator'],
    },
  ];

  const kpiCards = [
    { key: 'pendingOrders', label: t('home.kpi.pendingOrders'), value: stats?.pendingOrdersCount, icon: <PendingIcon />, color: theme.palette.warning.main },
    { key: 'revenue', label: t('home.kpi.revenueThisWeek'), value: stats ? `$${parseFloat(stats.revenueThisWeek).toLocaleString('es-AR', { minimumFractionDigits: 2 })}` : null, icon: <RevenueIcon />, color: '#4caf50' },
    { key: 'clients', label: t('home.kpi.totalClients'), value: stats?.totalClients, icon: <GroupIcon />, color: '#2196f3' },
    { key: 'ordersWeek', label: t('home.kpi.ordersThisWeek'), value: stats?.ordersThisWeek, icon: <ListIcon />, color: theme.palette.primary.main },
  ];

  const filteredActions = quickActions.filter((a) => a.roles.includes(user?.role));

  return (
    <Box>
      {/* Banner Carousel */}
      <Card sx={{ mb: 4, overflow: 'hidden' }}>
        <Box sx={{ position: 'relative', height: 200 }}>
          {banners.map((banner, index) => (
            <Box
              key={index}
              component="img"
              src={banner.img}
              alt={banner.alt}
              sx={{
                position: 'absolute', top: 0, left: 0,
                width: '100%', height: '100%', objectFit: 'cover',
                opacity: activeStep === index ? 1 : 0,
                transition: 'opacity 0.5s ease-in-out',
              }}
            />
          ))}
          <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, bgcolor: 'rgba(0,0,0,0.5)' }}>
            <MobileStepper
              variant="dots" steps={banners.length} position="static" activeStep={activeStep}
              sx={{ bgcolor: 'transparent', color: 'white' }}
              nextButton={<Button size="small" onClick={handleNext} sx={{ color: 'white' }}><KeyboardArrowRight /></Button>}
              backButton={<Button size="small" onClick={handleBack} sx={{ color: 'white' }}><KeyboardArrowLeft /></Button>}
            />
          </Box>
        </Box>
      </Card>

      {/* Welcome Banner */}
      <Card sx={{ mb: 4, color: 'white', backgroundImage: 'linear-gradient(135deg, #2f3238 0%, #4a4d52 100%)' }}>
        <CardContent sx={{ py: 4 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
            {t('app.welcome')}, {user?.clientInfo?.companyName || user?.username}!
          </Typography>
          <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
            {t('app.welcomeMessage')}
          </Typography>
        </CardContent>
      </Card>

      {/* KPI Dashboard (admin/operator only) */}
      {hasRole(['admin', 'operator']) && (
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={2}>
            {kpiCards.map((kpi) => (
              <Grid item xs={6} sm={3} key={kpi.key}>
                <Card>
                  <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{
                      width: 48, height: 48, borderRadius: '50%',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: `${kpi.color}20`, color: kpi.color, flexShrink: 0,
                    }}>
                      {kpi.icon}
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" noWrap>{kpi.label}</Typography>
                      {statsLoading
                        ? <Skeleton width={60} height={32} />
                        : <Typography variant="h5" sx={{ fontWeight: 700 }}>{kpi.value ?? '—'}</Typography>
                      }
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Ordering on/off control (admin only) */}
      {hasRole('admin') && (
        <Card sx={{ mb: 4, borderLeft: 6, borderColor: orderingEnabled ? 'success.main' : 'error.main' }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('home.orderingControl')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {orderingEnabled ? t('home.orderingOpenDesc') : t('home.orderingClosedDesc')}
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={orderingEnabled}
                  onChange={(e) => toggleOrdering(e.target.checked)}
                  disabled={orderingSaving}
                  color="success"
                />
              }
              label={orderingEnabled ? t('home.orderingOn') : t('home.orderingOff')}
              labelPlacement="start"
            />
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>{t('home.quickActions')}</Typography>
      <Grid container spacing={3}>
        {filteredActions.map((action) => (
          <Grid item xs={12} sm={6} md={3} key={action.title}>
            <Card
              sx={{
                height: '100%', cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 },
              }}
              onClick={() => navigate(action.path)}
            >
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Box sx={{
                  width: 80, height: 80, mx: 'auto', mb: 2, borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  bgcolor: `${action.color}20`, color: action.color,
                }}>
                  {action.icon}
                </Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>{action.title}</Typography>
                <Typography variant="body2" color="text.secondary">{action.description}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Announcements / editable message */}
      <Card sx={{ mt: 4 }}>
        {/* Card header */}
        <CardContent sx={{ pb: 0, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            📢 {t('home.news')}
          </Typography>
          {hasRole('admin') && !editing && (
            <Tooltip title={t('home.editMessage')}>
              <Button size="small" startIcon={<EditIcon />} onClick={startEditing}>
                {t('home.editMessage')}
              </Button>
            </Tooltip>
          )}
        </CardContent>

        {editing ? (
          /* ── Rich-text editor (admin) ── */
          <Box>
            <EditorToolbar editor={editor} />
            <Box sx={{ '& .tiptap': { outline: 'none' } }}>
              <EditorContent editor={editor} />
            </Box>
            <Divider />
            <Box sx={{ display: 'flex', gap: 1, p: 1.5, justifyContent: 'flex-end' }}>
              <Button size="small" startIcon={<CloseIcon />} onClick={cancelEditing} disabled={saving}>
                {t('home.cancelEdit')}
              </Button>
              <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={saveMessage} disabled={saving}>
                {saving ? '…' : t('home.saveMessage')}
              </Button>
            </Box>
          </Box>
        ) : (
          /* ── Rendered HTML ── */
          <CardContent>
            {homeMessage ? (
              <Box
                sx={{
                  '& a': { color: 'primary.main' },
                  '& h2': { mt: 1, mb: 0.5 },
                  '& h3': { mt: 1, mb: 0.5 },
                  '& ul, & ol': { pl: 3 },
                  '& p': { mt: 0, mb: 1 },
                }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(homeMessage) }}
              />
            ) : (
              <Typography variant="body1" color="text.secondary">
                {t('home.newsText')}
              </Typography>
            )}
          </CardContent>
        )}
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Home;
